import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../../api/client';
import { useToast } from '../../../hooks/useToast';
import { FiSave, FiPlus, FiMapPin, FiEdit2, FiTrash2 } from 'react-icons/fi';

const OrganisationProfile = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const [addresses, setAddresses] = useState([]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    addressType: 'registered', line1: '', line2: '', city: '', postalCode: '', countryIso2: 'GB', isPrimary: false,
  });

  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editAddressForm, setEditAddressForm] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.get('/organisations/current'),
      apiClient.get('/organisations/current/addresses'),
    ])
      .then(([profileRes, addressesRes]) => {
        const p = profileRes.data.data;
        setForm({
          legalName: p.legalName || '',
          tradingName: p.tradingName || '',
          companyRegistrationNumber: p.companyRegistrationNumber || '',
          vatRegistered: !!p.vatRegistered,
          vatNumber: p.vatNumber || '',
          payeReference: p.payeReference || '',
          accountsOfficeReference: p.accountsOfficeReference || '',
          payrollFrequencyDefault: p.payrollFrequencyDefault || 'monthly',
          financialYearStartMonth: p.financialYearStartMonth || 4,
        });
        setAddresses(addressesRes.data.data);
      })
      .catch((err) => showError(getErrorMessage(err, 'Could not load organisation profile')))
      .finally(() => setLoading(false));
  }, [showError]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.patch('/organisations/current', form);
      success('✅ Organisation profile updated');
    } catch (err) {
      showError(getErrorMessage(err, 'Could not save changes'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const { data } = await apiClient.post('/organisations/current/addresses', addressForm);
      setAddresses((prev) => [...prev, data.data]);
      setShowAddAddress(false);
      setAddressForm({ addressType: 'registered', line1: '', line2: '', city: '', postalCode: '', countryIso2: 'GB', isPrimary: false });
      success('✅ Address added');
    } catch (err) {
      showError(getErrorMessage(err, 'Could not add address'));
    }
  };

  const startEditAddress = (addr) => {
    setEditingAddressId(addr.id);
    setEditAddressForm({
      addressType: addr.addressType,
      line1: addr.line1 || '',
      line2: addr.line2 || '',
      city: addr.city || '',
      postalCode: addr.postalCode || '',
    });
  };

  const handleUpdateAddress = async (e, addressId) => {
    e.preventDefault();
    try {
      const { data } = await apiClient.patch(`/organisations/current/addresses/${addressId}`, editAddressForm);
      setAddresses((prev) => prev.map((a) => (a.id === addressId ? data.data : a)));
      setEditingAddressId(null);
      success('✅ Address updated');
    } catch (err) {
      showError(getErrorMessage(err, 'Could not update address'));
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await apiClient.delete(`/organisations/current/addresses/${addressId}`);
      setAddresses((prev) => prev.filter((a) => a.id !== addressId));
      success('✅ Address deleted');
    } catch (err) {
      showError(getErrorMessage(err, 'Could not delete address'));
    }
  };

  if (loading || !form) {
    return <div className="dashboard-page"><p>Loading...</p></div>;
  }

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Organisation Profile</h1>
        <p>Manage your company's core details</p>
      </div>

      <section className="page-section form-section">
        <form onSubmit={handleSave}>
          <div className="form-grid">
            <div className="form-field">
              <label>Legal Name *</label>
              <input name="legalName" value={form.legalName} onChange={handleChange} required />
            </div>
            <div className="form-field">
              <label>Trading Name</label>
              <input name="tradingName" value={form.tradingName} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Company Registration Number</label>
              <input name="companyRegistrationNumber" value={form.companyRegistrationNumber} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Default Payroll Frequency</label>
              <select name="payrollFrequencyDefault" value={form.payrollFrequencyDefault} onChange={handleChange}>
                <option value="monthly">Monthly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div className="form-field">
              <label>Financial Year Start Month</label>
              <select name="financialYearStartMonth" value={form.financialYearStartMonth} onChange={handleChange}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                <input type="checkbox" name="vatRegistered" checked={form.vatRegistered} onChange={handleChange} style={{ width: 'auto' }} />
                VAT Registered
              </label>
            </div>
            {form.vatRegistered && (
              <div className="form-field">
                <label>VAT Number</label>
                <input name="vatNumber" value={form.vatNumber} onChange={handleChange} placeholder="GB123456789" />
              </div>
            )}
            <div className="form-field">
              <label>PAYE Reference</label>
              <input name="payeReference" value={form.payeReference} onChange={handleChange} placeholder="123/AB456" />
            </div>
            <div className="form-field">
              <label>Accounts Office Reference</label>
              <input name="accountsOfficeReference" value={form.accountsOfficeReference} onChange={handleChange} />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <FiSave size={16} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </section>

      <section className="page-section">
        <div className="section-header">
          <h2>Addresses</h2>
          <button
            className="btn btn-sm btn-primary"
            style={{ width: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            onClick={() => setShowAddAddress((s) => !s)}
          >
            <FiPlus size={14} /> {showAddAddress ? 'Cancel' : 'Add Address'}
          </button>
        </div>

        {showAddAddress && (
          <form onSubmit={handleAddAddress} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>Address Type</label>
              <select value={addressForm.addressType} onChange={(e) => setAddressForm(p => ({ ...p, addressType: e.target.value }))}>
                <option value="registered">Registered</option>
                <option value="trading">Trading</option>
                <option value="billing">Billing</option>
              </select>
            </div>
            <div className="form-field full-width">
              <label>Address Line 1 *</label>
              <input required value={addressForm.line1} onChange={(e) => setAddressForm(p => ({ ...p, line1: e.target.value }))} />
            </div>
            <div className="form-field full-width">
              <label>Address Line 2</label>
              <input value={addressForm.line2} onChange={(e) => setAddressForm(p => ({ ...p, line2: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>City</label>
              <input value={addressForm.city} onChange={(e) => setAddressForm(p => ({ ...p, city: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Postal Code</label>
              <input value={addressForm.postalCode} onChange={(e) => setAddressForm(p => ({ ...p, postalCode: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Country (ISO2)</label>
              <input value={addressForm.countryIso2} onChange={(e) => setAddressForm(p => ({ ...p, countryIso2: e.target.value.toUpperCase() }))} maxLength={2} />
            </div>
            <div className="form-field full-width form-actions">
              <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save Address</button>
            </div>
          </form>
        )}

        {addresses.length === 0 ? (
          <p style={{ color: '#6b7280' }}>No addresses on file yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {addresses.map((addr) => (
              <div key={addr.id} style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                {editingAddressId === addr.id ? (
                  <form onSubmit={(e) => handleUpdateAddress(e, addr.id)} className="form-grid">
                    <div className="form-field">
                      <label>Address Type</label>
                      <select value={editAddressForm.addressType} onChange={(e) => setEditAddressForm(p => ({ ...p, addressType: e.target.value }))}>
                        <option value="registered">Registered</option>
                        <option value="trading">Trading</option>
                        <option value="billing">Billing</option>
                      </select>
                    </div>
                    <div className="form-field full-width">
                      <label>Address Line 1 *</label>
                      <input required value={editAddressForm.line1} onChange={(e) => setEditAddressForm(p => ({ ...p, line1: e.target.value }))} />
                    </div>
                    <div className="form-field full-width">
                      <label>Address Line 2</label>
                      <input value={editAddressForm.line2} onChange={(e) => setEditAddressForm(p => ({ ...p, line2: e.target.value }))} />
                    </div>
                    <div className="form-field">
                      <label>City</label>
                      <input value={editAddressForm.city} onChange={(e) => setEditAddressForm(p => ({ ...p, city: e.target.value }))} />
                    </div>
                    <div className="form-field">
                      <label>Postal Code</label>
                      <input value={editAddressForm.postalCode} onChange={(e) => setEditAddressForm(p => ({ ...p, postalCode: e.target.value }))} />
                    </div>
                    <div className="form-field full-width form-actions">
                      <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save</button>
                      <button type="button" className="btn btn-outline" style={{ width: 'auto' }} onClick={() => setEditingAddressId(null)}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <FiMapPin size={18} style={{ color: '#6b7280', flexShrink: 0, marginTop: '0.2rem' }} />
                      <div>
                        <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{addr.addressType}{addr.isPrimary ? ' (Primary)' : ''}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                          {[addr.line1, addr.line2, addr.city, addr.postalCode].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>
                    <div className="actions">
                      <button className="action-icon edit" title="Edit" onClick={() => startEditAddress(addr)}>
                        <FiEdit2 size={16} />
                      </button>
                      <button className="action-icon delete" title="Delete" onClick={() => handleDeleteAddress(addr.id)}>
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </motion.div>
  );
};

export default OrganisationProfile;